import React, { memo, useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { Divider, Interpolate, Link } from '@wg/wows-react-uikit';

import { sendEventToGTM } from '~/helpers/analytics';
import { showRulesDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { isPreModerateNameTag } from '~/helpers/moderation';
import useMount from '~/hooks/useMount';
import { useAppDispatch, useAppSelector } from '~/store';
import { getBalanceThunk } from '~/Actions/ActionAccount';
import { actionsClanCreate, checkNameOrTagAvailability, creationClan } from '~/Actions/ActionClanCreate';

import StepCSSTransitionGroup from '~/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import styles from './ClanCreate.scss';
import ClanCreateFooter from './ClanCreateFooter';
import ClanInputWrapper from './ClanInputWrapper';

import type { ICreateErrorsCommon } from '~/Actions/ActionClanCreate';
import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { RootState } from '~/store';
import type { IApiError } from '~/types/api';

const stateSelector = (state: RootState) => ({
  error: state.clanCreate.error,
  isProcessing: state.clanCreate.isProcessing,
  name: state.clanCreate.name,
  tag: state.clanCreate.tag,
});

const ClanCreate: React.FC = () => {
  const { error, isProcessing, name, tag } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();

  useMount(() => {
    void dispatch(getBalanceThunk());

    return () => {
      dispatch(actionsClanCreate.setInitialStateCreationClan());
    };
  });

  const onValidateOnServer = useCallback(
    (field: keyof IClanCreateStateFields, value: string) => {
      dispatch(actionsClanCreate.startCheckAvailabilityCreationClan(field));
      return dispatch(checkNameOrTagAvailability(field, value, actionsClanCreate.finishCheckAvailabilityCreationClan));
    },
    [dispatch],
  );

  const onUpdateFieldValue = useCallback(
    (field: keyof IClanCreateStateFields, value: string, error: IApiError) => {
      return dispatch(actionsClanCreate.updateFieldValueCreationClan(field, value, error));
    },
    [dispatch],
  );

  const onClickClansRules = useCallback(() => {
    sendEventToGTM({
      category: 'Create page',
      action: 'Click: Show rules dialog',
      label: 'Clan rules action link',
    });
    dispatch(showRulesDialog());
  }, [dispatch]);

  const onCreateClan = useCallback(() => {
    sendEventToGTM({
      category: 'Create page',
      action: 'Click: Create clan button',
      label: 'Create clan button in create page',
    });
    void dispatch(creationClan());
  }, [dispatch]);

  const inputsErrors = useMemo(
    () => ({
      tag: ((error as ICreateErrorsCommon)?.errors?.tag || [])[0] || null,
      name: ((error as ICreateErrorsCommon)?.errors?.name || [])[0] || null,
    }),
    [error],
  );

  const clanRulesComponent = (
    <Link className={styles.link} isActionLink={true} arrow={''} onClick={onClickClansRules}>
      {t('Правилами кланов')}
    </Link>
  );

  return (
    <div className={styles.clanCreate}>
      <StepCSSTransitionGroup level={1}>
        <ClanInputWrapper
          className={styles.inputTag}
          error={inputsErrors.tag || tag.error}
          field={'tag'}
          isDisabled={isProcessing}
          isFocusOn
          isValidating={tag.isValidating}
          value={tag.value}
          onChange={onUpdateFieldValue}
          onValidateOnServer={onValidateOnServer}
        />
      </StepCSSTransitionGroup>
      <StepCSSTransitionGroup level={2}>
        <ClanInputWrapper
          className={styles.inputName}
          error={inputsErrors.name || name.error}
          field={'name'}
          isDisabled={isProcessing}
          isValidating={name.isValidating}
          value={name.value}
          onChange={onUpdateFieldValue}
          onValidateOnServer={onValidateOnServer}
        />
      </StepCSSTransitionGroup>
      <StepCSSTransitionGroup level={3}>
        <Divider key="divider-top" type="major" />
        <div key="message" className={styles.message}>
          {t('Вы сможете изменять название после создания клана, нажав на тег и название на экране «Мой клан».')}
          &ensp;
          {t('Изменение тега и названия осуществляется за дублоны.')}
          {isPreModerateNameTag && (
            <>
              &ensp;
              {t('Название и тег вашего клана обновятся, как только они будут одобрены модератором.')}
            </>
          )}
        </div>
        <Divider key="divider-bottom" type="major" />
      </StepCSSTransitionGroup>
      <StepCSSTransitionGroup level={4}>
        <div key="rules" className={styles.rules}>
          <Interpolate str={t('Создавая клан, вы соглашаетесь с %(clanRules)s')} clanRules={clanRulesComponent} />
        </div>
        <ClanCreateFooter
          error={error as IApiError}
          isProcessing={isProcessing}
          name={name}
          tag={tag}
          onCreateClan={onCreateClan}
        />
      </StepCSSTransitionGroup>
    </div>
  );
};

export default memo(ClanCreate);
